import { twMerge } from "tailwind-merge";
import { SVGs } from "../assets";
import * as React from "react";
import Text from "./Text";

export type ButtonColor =
  | "primary"
  | "secondary"
  | "secondaryDark"
  | "critical"
  | "disabled"
  | "tertiary";
export type ButtonVariant = "solid" | "outline" | "text";

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  color?: ButtonColor;
  variant?: ButtonVariant;
  icon?: React.ReactNode;
  loading?: boolean;
};

const base = twMerge(
  "inline-flex h-[40px] items-center justify-center gap-2 rounded-[30px] px-[20px] font-sans text-[14px] font-semibold leading-5 text-center align-middle",
  "transition-[transform,box-shadow,background-color,color,opacity] active:translate-y-px ",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background ",
  "disabled:pointer-events-none",
);

const textBase = twMerge(
  "inline-flex items-center justify-center gap-2 font-sans text-[14px] font-semibold leading-5 text-center align-middle",
  "transition-[transform,color,opacity] active:translate-y-px",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background ",
  "disabled:pointer-events-none",
);

function isThenable(v: unknown): v is Promise<unknown> {
  return v != null && typeof (v as { then?: unknown }).then === "function";
}

function isNewVariant(v: unknown): v is ButtonVariant {
  return v === "solid" || v === "outline" || v === "text";
}

const solidClassByColor: Record<ButtonColor, string> = {
  primary: "bg-primary text-text-primary shadow-soft hover:brightness-105",
  secondary:
    "bg-background text-text-primary shadow-sm hover:brightness-[0.99]",
  secondaryDark:
    "bg-secondary text-text-surface shadow-sm hover:brightness-[0.99]",
  critical:
    "bg-red-600 text-white shadow-sm hover:bg-red-700 focus-visible:ring-red-500/30",
  disabled:
    "bg-button-disabled text-text-disabled border border-button-disabled shadow-sm",
  tertiary: "bg-surface text-text-primary  hover:bg-primary/10",
};

const outlineClassByColor: Record<ButtonColor, string> = {
  primary:
    "bg-transparent text-primary border border-primary hover:bg-primary/10",
  secondary:
    "bg-transparent text-text-primary border border-text-primary hover:bg-background/60",
  secondaryDark:
    "bg-transparent text-text-surface border border-text-surface hover:bg-secondary/10",
  critical:
    "bg-transparent text-red-600 border border-red-600 hover:bg-red-600/10",
  disabled: "bg-transparent text-text-disabled border border-button-disabled",
  tertiary:
    "bg-transparent text-surface border border-text-surface hover:bg-text-surface/10",
};

const textClassByColor: Record<ButtonColor, string> = {
  primary: "bg-transparent text-primary hover:underline",
  secondary: "bg-transparent text-text-primary hover:underline",
  secondaryDark: "bg-transparent text-text-surface hover:underline",
  critical: "bg-transparent text-red-600 hover:underline",
  disabled: "bg-transparent text-text-disabled",
  tertiary: "bg-transparent text-surface hover:underline",
};

export type ButtonRef = {
  click: (e?: any) => Promise<void> | void;
};

const Button = React.forwardRef<ButtonRef, ButtonProps>((props, ref) => {
  const {
    color = "primary",
    variant = "solid",
    icon,
    className,
    type,
    onClick,
    ...rest
  } = props;
  const [pending, startTransition] = React.useTransition();
  const loading = props.loading || pending;
  const resolvedVariant: ButtonVariant = isNewVariant(variant)
    ? variant
    : "solid";
  const disabledByProp = rest.disabled || color === "disabled";
  const resolvedColor: ButtonColor = disabledByProp ? "disabled" : color;
  const variantClass =
    resolvedVariant === "outline"
      ? outlineClassByColor[resolvedColor]
      : resolvedVariant === "text"
        ? textClassByColor[resolvedColor]
        : solidClassByColor[resolvedColor];

  const handleOnClick = React.useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!onClick) return;
      const result = onClick(e) as unknown;
      if (!isThenable(result)) return;
      startTransition(async () => {
        await result;
      });
    },
    [onClick, startTransition],
  );

  React.useImperativeHandle(ref, () => ({ click: handleOnClick }), [
    handleOnClick,
  ]);

  return (
    <button
      ref={ref as any}
      type={type ?? "button"}
      className={twMerge(
        resolvedVariant === "text" ? textBase : base,
        variantClass,
        className,
      )}
      disabled={disabledByProp || loading}
      {...rest}
      onClick={handleOnClick}
    >
      {loading ? (
        <SVGs.Spinner className="w-[18px] h-[18px] animate-spin" />
      ) : (
        <>
          <Text className="text-inherit">{rest.children}</Text>
          {icon}
        </>
      )}
    </button>
  );
});
Button.displayName = "Button";

export default Button;
